import Button from '@rio-cloud/rio-uikit/Button';
import Table, { TableBody, TableColumn, TableHeader, TableHeaderColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { useAllowAssetDeleteWithActiveContractsToggle } from '../../../../../configuration/featureToggle/toggleHooks';
import { useIsLatamTenant, useIsTestTenant } from '../../../../../configuration/login/loginSlice';
import { useGetActiveContractsQuery } from '../../../../services/contractsApi';
import { useCheckActiveContractsQuery } from '../../../../services/latamContractsApi';

interface ActiveContractsWarningProps {
    assetId: string;
    deletionMode: boolean;
}

export const ActiveContractsWarning = ({ assetId, deletionMode }: ActiveContractsWarningProps) => {
    const isLatamTenant = useIsLatamTenant();
    const { data: activeContracts } = useGetActiveContractsQuery(assetId, { skip: isLatamTenant });
    const { data: activeLatamContracts } = useCheckActiveContractsQuery(assetId, { skip: !isLatamTenant });
    const allowAssetDeleteWithActiveContracts = useAllowAssetDeleteWithActiveContractsToggle();
    const isTestTenant = useIsTestTenant();
    const activeContractsList = Array.isArray(activeContracts) ? activeContracts : [];
    const activeLatamContractsList = Array.isArray(activeLatamContracts) ? activeLatamContracts : [];
    const tableRows = isLatamTenant
        ? activeLatamContractsList.map(({ product_name, subscription_id, period_description }) => ({
              key: subscription_id,
              service: `${product_name} ${period_description}`,
          }))
        : activeContractsList.map(({ product_level_name, ends_at }) => ({
              key: `${product_level_name}-${ends_at}`,
              service: product_level_name,
              endDate: moment(ends_at).format('D.M.Y'),
          }));

    if (!tableRows.length) {
        return null;
    }

    return (
        <div className='alert alert-danger display-flex flex-column gap-15 margin-top-10'>
            <div className='display-flex gap-10'>
                <span className={'text-color-danger text-size-h4 rioglyph rioglyph-error-sign'} />
                <div>
                    <strong className='text-size-16'>
                        {deletionMode && !allowAssetDeleteWithActiveContracts && !isTestTenant ? (
                            <FormattedMessage
                                id={'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.title'}
                            />
                        ) : (
                            <FormattedMessage
                                id={'intl-msg:asset.archive-confirmation-dialog.content.active-contracts.title'}
                            />
                        )}
                    </strong>
                    <div>
                        {deletionMode ? (
                            <FormattedMessage
                                id={'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.hint'}
                            />
                        ) : (
                            <FormattedMessage
                                id={'intl-msg:asset.archive-confirmation-dialog.content.active-contracts.hint'}
                            />
                        )}
                        <div className='margin-y-10'>
                            <Table rowKey='key' border={true} rounded={true} condensed rowSeparatorWidth={0}>
                                <TableHeader>
                                    <TableHeaderColumn columnKey='service'>
                                        <FormattedMessage
                                            id={
                                                'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.service'
                                            }
                                        />
                                    </TableHeaderColumn>
                                    {!isLatamTenant && (
                                        <TableHeaderColumn columnKey='endDate'>
                                            <FormattedMessage
                                                id={
                                                    'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.end-date'
                                                }
                                            />
                                        </TableHeaderColumn>
                                    )}
                                </TableHeader>
                                <TableBody>
                                    {tableRows.map(row => (
                                        <TableRow key={row.key} id={row.key}>
                                            <TableColumn columnKey='service'>{row.service}</TableColumn>
                                            {!isLatamTenant && (
                                                <TableColumn columnKey='endDate'>{row.endDate}</TableColumn>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        {isLatamTenant && (
                            <div style={{ display: 'flex', justifyContent: 'right' }}>
                                <Button
                                    bsStyle={Button.PRIMARY}
                                    autoTrackingKey={`intl-msg:asset.${deletionMode ? 'delete' : 'archive'}-confirmation-dialog.button.contact-support.single`}
                                    onClick={() =>
                                        window.open(
                                            encodeURI(
                                                `https://contact-form.rio.cloud/contact?opener=${deletionMode ? 'delete' : 'archive'} asset ${assetId}`
                                            ),
                                            '_blank'
                                        )
                                    }
                                >
                                    <FormattedMessage
                                        id={`intl-msg:asset.${deletionMode ? 'delete' : 'archive'}-confirmation-dialog.button.contact-support`}
                                    />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
